import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress,
  Chip,
  Pagination,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { providerApi } from '../../services/api';

const PAGE_SIZE = 10;

/**
 * Pro reviews (design §8): summary header (average, count, 1..5 distribution
 * bars) + paged review list, with a public reply per review.
 *
 * Customer submission now exists (verified reviews, tied to completed bookings), so the empty
 * state simply says reviews will appear here rather than promising a future feature.
 *
 * A reply an admin has hidden is flagged rather than silently missing: it is the one moderation
 * state a provider is told about, so they are not left believing their answer is live.
 */
export default function ReviewsPage() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (pageIndex) => {
    setLoading(true);
    setError('');
    try {
      const [summaryData, pageData] = await Promise.all([
        providerApi.getMyReviewsSummary(),
        providerApi.getMyReviews(pageIndex, PAGE_SIZE),
      ]);
      setSummary(summaryData);
      setReviews(pageData);
    } catch (e) {
      setError(t('reviewsPage.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(page);
  }, [page, load]);

  // reviewId currently being replied to, plus the draft text for it.
  const [replyingTo, setReplyingTo] = useState(null);
  const [draft, setDraft] = useState('');
  const [replyError, setReplyError] = useState('');

  const openReply = (review) => {
    setReplyingTo(review.id);
    setDraft(review.replyBody || '');
    setReplyError('');
  };

  const saveReply = async (reviewId) => {
    setReplyError('');
    try {
      await providerApi.upsertReviewReply(reviewId, draft.trim());
      setReplyingTo(null);
      setDraft('');
      await load(page);
    } catch (e) {
      setReplyError(t('reviewsPage.replySaveFailed'));
    }
  };

  const removeReply = async (reviewId) => {
    setReplyError('');
    try {
      await providerApi.deleteReviewReply(reviewId);
      await load(page);
    } catch (e) {
      setReplyError(t('reviewsPage.replySaveFailed'));
    }
  };

  if (loading && !reviews) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const count = summary?.count ?? 0;
  const distribution = summary?.distribution || {};
  const maxStarCount = Math.max(1, ...Object.values(distribution).map(Number));
  const totalPages = reviews?.totalPages ?? 1;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2.5 }}>{t('reviewsPage.title')}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ textAlign: 'center', minWidth: 140 }}>
            <Typography variant="h2" fontWeight={800} lineHeight={1}>
              {summary?.average != null ? summary.average.toFixed(1) : '—'}
            </Typography>
            <Rating value={summary?.average || 0} precision={0.1} readOnly size="small" />
            <Typography variant="caption" color="text.secondary" display="block">
              {t('reviewsPage.countLabel', { count })}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            {[5, 4, 3, 2, 1].map((star) => (
              <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ width: 12 }}>{star}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={(Number(distribution[star] || 0) / maxStarCount) * 100}
                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ width: 24 }}>
                  {distribution[star] || 0}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {count === 0 ? (
        <Card>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <RateReviewOutlinedIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              {t('reviewsPage.empty')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Stack divider={<Divider />} spacing={2}>
              {(reviews?.content || []).map((review) => (
                <Box key={review.id} sx={{ display: 'flex', gap: 2 }}>
                  <Avatar src={review.reviewerAvatarUrl || undefined}>
                    {(review.reviewerName || '?').charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2">{review.reviewerName || t('reviewsPage.anonymous')}</Typography>
                      <Rating value={review.rating} readOnly size="small" />
                      <Typography variant="caption" color="text.secondary">
                        {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : ''}
                      </Typography>
                    </Box>
                    {review.comment && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{review.comment}</Typography>
                    )}

                    {review.replyHiddenByAnipaw && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={t('reviewsPage.replyHiddenByAnipaw')}
                        sx={{ mt: 1 }}
                      />
                    )}

                    {review.replyBody && replyingTo !== review.id && (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" fontWeight={700} display="block">
                          {t('reviewsPage.replyTitle')}
                        </Typography>
                        <Typography variant="body2">{review.replyBody}</Typography>
                      </Box>
                    )}

                    {replyingTo === review.id ? (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          inputProps={{ maxLength: 1000 }}
                          placeholder={t('reviewsPage.replyPlaceholder')}
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {t('reviewsPage.replyPublicNote')}
                        </Typography>
                        {replyError && <Alert severity="error" sx={{ mt: 1 }}>{replyError}</Alert>}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!draft.trim()}
                            onClick={() => saveReply(review.id)}
                          >
                            {t('reviewsPage.replySave')}
                          </Button>
                          <Button size="small" onClick={() => setReplyingTo(null)}>
                            {t('reviewsPage.replyCancel')}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button size="small" onClick={() => openReply(review)}>
                          {review.hasReply ? t('reviewsPage.replyEdit') : t('reviewsPage.reply')}
                        </Button>
                        {review.hasReply && (
                          <Button size="small" color="error" onClick={() => removeReply(review.id)}>
                            {t('reviewsPage.replyRemove')}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={totalPages} page={page + 1} onChange={(e, value) => setPage(value - 1)} />
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
